import { useCallback } from 'react';
import { useSubscription } from '../context/SubscriptionContext';
import type { EntitlementId, ProductLine } from '@shared/types';

interface ShowPaywallOptions {
  /** Which surface triggered the paywall — recorded on the impression for analytics. */
  source?: string;
  /** Which product line the paywall should lead with (three-product scaffolding). */
  product?: ProductLine;
  onPurchaseComplete?: () => void;
  onPurchaseError?: (error: Error) => void;
  onDismiss?: () => void;
}

interface UsePaywallReturn {
  /** Whether the user has any active subscription */
  isPremium: boolean;
  /** True for any paid key. @deprecated for feature gates — prefer the
   *  capability predicates so a call site reads as what it gates. */
  isDataConnect: boolean;
  /** True when the user's entitlements grant the spending tools (every paid key). */
  hasSpendingTools: boolean;
  /** True when the user's entitlements grant the debt tools — every paid key
   *  since 2026-10-07 (one product), so it never differs from hasSpendingTools. */
  hasDebtTools: boolean;
  /** The highest active entitlement, or null if none */
  highestEntitlement: EntitlementId | null;
  /** Whether subscription status is still loading */
  loading: boolean;
  /** Whether subscription info is ready */
  isReady: boolean;
  /** Show the custom paywall modal */
  showPaywall: (options?: ShowPaywallOptions) => void;
  /** Hide the custom paywall modal */
  hidePaywall: () => void;
  /** Whether the custom paywall is currently open */
  isPaywallOpen: boolean;
  /**
   * Gate a spending tools feature. Returns true if the user has a paid key,
   * otherwise shows the paywall and returns false.
   */
  gateSpendingToolsFeature: () => boolean;
  /**
   * Gate a debt tools feature. One product (2026-10-07): the same check as
   * gateSpendingToolsFeature, kept so debt call sites read as what they gate;
   * the paywall impression is tagged with its own source for the funnels.
   */
  gateDebtToolsFeature: () => boolean;
  /** Refresh subscription status from RevenueCat — returns true if active */
  refreshSubscription: () => Promise<boolean>;
}

/**
 * Hook for convenient paywall and entitlement access control.
 * Uses RevenueCat to check entitlements.
 */
export const usePaywall = (): UsePaywallReturn => {
  const {
    isPremium,
    isDataConnect,
    hasSpendingTools,
    hasDebtTools,
    highestEntitlement,
    loading,
    isReady,
    showPaywall,
    hidePaywall,
    paywallState,
    refreshSubscription,
  } = useSubscription();

  /** Gate a Spending Tools feature; shows the paywall if the user lacks it. */
  const gateSpendingToolsFeature = useCallback((): boolean => {
    if (hasSpendingTools) {
      return true;
    }
    showPaywall({ source: 'spending_tools_gate', product: 'spending' });
    return false;
  }, [hasSpendingTools, showPaywall]);

  /** Gate a debt tools feature; the one membership includes it, so the
   *  paywall leads with the one product sold. */
  const gateDebtToolsFeature = useCallback((): boolean => {
    if (hasDebtTools) {
      return true;
    }
    showPaywall({ source: 'debt_tools_gate', product: 'spending' });
    return false;
  }, [hasDebtTools, showPaywall]);

  return {
    isPremium,
    isDataConnect,
    hasSpendingTools,
    hasDebtTools,
    highestEntitlement,
    loading,
    isReady,
    showPaywall,
    hidePaywall,
    isPaywallOpen: paywallState.isOpen,
    gateSpendingToolsFeature,
    gateDebtToolsFeature,
    refreshSubscription,
  };
};
